import { useState } from "react";

import { Button, Checkbox, InfoModal, LoadingSpinner } from "@newsela/angelou";
import { isEmpty, isNil } from "@s-libs/micro-dash";
import PropTypes from "prop-types";

import globalToasterRef from "@/base/globalToasterRef";
import AUTH_PROVIDER_NAME_TRANSFORMATIONS from "@/constants/AUTH_PROVIDER_NAME_TRANSFORMATIONS";
import useUser from "@/users/useUser";
import useClassroomSync from "@/classrooms/useClassroomSync";
import ClassroomSelectionBox from "../ClassroomSelectionBox";
import {
  archiveContainerCss,
  centerContainerCss,
  modalCss,
  resultsContainerCss,
} from "./ClassroomSyncModal.style";
import { EVENT_TOAST_FAIL } from "./ClassroomSyncModal.utils";

const propTypes = {
  modalProps: PropTypes.object.isRequired,
  setActive: PropTypes.func.isRequired,
  onRosterSyncStarted: PropTypes.func.isRequired,
  canSyncClassrooms: PropTypes.bool.isRequired,
};

const ClassroomSyncModal = ({
  modalProps,
  setActive,
  onRosterSyncStarted,
  canSyncClassrooms,
}) => {
  const { data: user } = useUser();
  const {
    rosterClassrooms: {
      data: rosterClassrooms = [],
      isLoading: isLoadingRosterClassrooms,
    },
    userSchools: { data: userSchools = [], isLoading: isLoadingUserSchools },
    districtBulkProcessingConfiguration: {
      isLoading: isLoadingDistrictBulkProcessingConfiguration,
    },
    mutateSyncRosterClassroomsStates: { isPending: isSyncingRosterClassrooms },
    archiveAllActiveBulkClassrooms,
    selectedRosterClassrooms,
    onClassroomSelection,
    isUserReSyncing,
    hasSelectedAnyClassroomForSyncing,
    isFormValid,
    syncRosterClassrooms,
    activeBulkSyncedClassrooms,
    hasSelectedNewClassroomForSyncing,
  } = useClassroomSync();

  const [shouldArchiveBulkClasses, setShouldArchiveBulkClasses] =
    useState(false);

  const hasActiveBulkClassrooms = !isEmpty(activeBulkSyncedClassrooms);
  const canArchiveClassrooms =
    canSyncClassrooms &&
    hasActiveBulkClassrooms &&
    hasSelectedAnyClassroomForSyncing;
  const isLoading =
    isLoadingRosterClassrooms ||
    isLoadingUserSchools ||
    isSyncingRosterClassrooms ||
    isLoadingDistrictBulkProcessingConfiguration;
  const syncModalTitle =
    "Sync classes" +
    (AUTH_PROVIDER_NAME_TRANSFORMATIONS[user.auth_type]
      ? " from " + AUTH_PROVIDER_NAME_TRANSFORMATIONS[user.auth_type]
      : "");

  const syncBulkClassrooms = async () => {
    const onSyncSuccess = (authProvider) => {
      onRosterSyncStarted(authProvider, shouldArchiveBulkClasses);
      setActive(false);
    };

    try {
      await syncRosterClassrooms();
      onSyncSuccess(user.auth_type);
    } catch (_) {
      globalToasterRef.current.displayToast(EVENT_TOAST_FAIL);
    }
  };

  const onSubmit = async () => {
    if (shouldArchiveBulkClasses) {
      try {
        await archiveAllActiveBulkClassrooms();
      } catch (error) {
        console.error("failed to archive bulk classrooms", error);
      }
    }
    await syncBulkClassrooms();
  };

  const getArchiveLabel = (bulkType) => {
    if (bulkType === "clever") {
      return "Archive my Clever classes";
    } else {
      return "Archive my district rostered classes";
    }
  };

  const infoModalProps = {
    title: syncModalTitle,
    labelId: "SyncClasses",
    __cssFor: modalCss,
    footer: (
      <>
        {hasActiveBulkClassrooms && canSyncClassrooms && (
          <div css={archiveContainerCss}>
            <Checkbox
              label={getArchiveLabel(user.auth_type)}
              checked={shouldArchiveBulkClasses}
              onClick={() =>
                setShouldArchiveBulkClasses(
                  (shouldArchiveBulkClasses) => !shouldArchiveBulkClasses,
                )
              }
              disabled={!canArchiveClassrooms}
              dataProps={{
                "data-testid": "archive-checkbox",
              }}
              __cssFor={{ root: archiveContainerCss.checkbox }}
            />
          </div>
        )}
        <Button
          flavor={Button.flavors.secondary}
          onClick={() => setActive(false)}
          __cssFor={{ root: { marginLeft: "auto" } }}
        >
          Cancel
        </Button>
        <Button
          flavor={Button.flavors.primary}
          disabled={isLoading || !isFormValid}
          onClick={onSubmit}
          dataProps={{
            "data-testid": "sync-classes-button",
          }}
        >
          {isUserReSyncing && !hasSelectedNewClassroomForSyncing
            ? "Sync again"
            : "Sync classes"}
        </Button>
      </>
    ),
    ...modalProps,
  };

  if (isLoading) {
    return (
      <InfoModal {...infoModalProps}>
        <div css={centerContainerCss} data-testid="loading-spinner">
          <LoadingSpinner />
        </div>
      </InfoModal>
    );
  }

  if (rosterClassrooms.length === 0) {
    return (
      <InfoModal {...infoModalProps}>
        <div css={centerContainerCss}>
          <span>No results found</span>
        </div>
      </InfoModal>
    );
  }

  return (
    <InfoModal {...infoModalProps}>
      <h2>Select classes to sync or import:</h2>
      <div css={resultsContainerCss}>
        {rosterClassrooms.map((classroom, index) => (
          <ClassroomSelectionBox
            key={classroom.id}
            classroom={selectedRosterClassrooms[classroom.id] || classroom}
            editables={{
              grade: isNil(classroom.grade),
              subject_slugs: isEmpty(classroom.subject_slugs),
              school: isEmpty(classroom.school),
            }}
            defaultValue={classroom}
            schools={userSchools}
            onChange={onClassroomSelection}
            qaSelectors={{
              schoolQASelector: `settings-classroom-selection-school-${index}`,
              gradeQASelector: `settings-classroom-selection-grade-level-${index}`,
              subjectQASelector: `settings-classroom-selection-subject-${index}`,
            }}
          />
        ))}
      </div>
    </InfoModal>
  );
};

ClassroomSyncModal.propTypes = propTypes;

export default ClassroomSyncModal;
